<script setup lang="ts">
	import { Form } from 'ant-design-vue'
	import useAccountApi from '~/services/apis/account'
	import usePermissionsApi from '~/services/apis/permissions'
	import type { InviteTeamPayload } from '~/types'
	import type { FormRecordsContexts } from '@/composables/useFormsFields'

	const emit = defineEmits<{
		(arg: 'update:openInvite', param: boolean): void
	}>()
	const props = defineProps<{
		openInvite: boolean
	}>()

	const isInvitingMember = ref(false)
	const isDisabling = ref(false)
	const disableTeamModal = ref(false)
	const loadingTable = ref(false)
	const { Roles, User } = reactor(useUser())
	const { hasAnyPermission } = reactor(usePermissionStore())

	const { InviteTeamMember, GetTeamMembers } = useAccountApi()
	const { GetAllRoles } = usePermissionsApi()
	const { replace } = useRouter()
	const teamList = ref<TeamMembersResponse[]>([])
	const query = computed(() => useRoute().query)
	const form = reactive<InviteTeamPayload>({
		business_id: User.value?.customer.id as string,
		callback_url: '',
		first_name: '',
		last_name: '',
		password: '',
		email: '',
		role: undefined,
		phone: ''
	})
	const search = shallowRef('')
	const teamColumns = [
		{
			title: 'S/N',
			dataIndex: 'sn',
			key: 'sn'
		},
		{
			title: 'Full Name',
			dataIndex: 'full_name',
			key: 'full_name'
		},
		{
			title: 'Email Address',
			dataIndex: 'email',
			key: 'email'
		},
		{
			title: 'Phone Number',
			dataIndex: 'phone',
			key: 'phone'
		},
		{
			title: 'Role',
			dataIndex: 'role',
			key: 'role'
		},
		{
			title: 'Status',
			key: 'status',
			dataIndex: 'status'
		},
		{
			title: 'Actions',
			key: 'actions',
			dataIndex: 'actions'
		}
	]
	const { resetFields } = Form.useForm(form)

	const formItems = computed<FormRecordsContexts>(() => ({
		first_name: {
			type: 'text',
			label: 'First name',
			form_name: 'first_name',
			placeholder: 'Enter first name',
			value: form.first_name,
			rules: { required: true, min: 2, max: 50 }
		},
		last_name: {
			type: 'text',
			label: 'Last name',
			form_name: 'last_name',
			placeholder: 'Enter last name',
			value: form.last_name,
			rules: { required: true, min: 2, max: 50 }
		},
		email: {
			type: 'email',
			label: 'Email address',
			form_name: 'email',
			placeholder: 'Enter email address',
			value: form.email,
			rules: { required: true, email: true }
		},
		phone: {
			type: 'number',
			label: 'Phone number',
			form_name: 'phone',
			placeholder: 'Enter phone',
			value: form.phone,
			rules: { min: 11, max: 11, message: 'Phone number must be 11 digits', required: true }
		},
		role: {
			type: 'select',
			label: 'Role of a team member',
			form_name: 'role',
			placeholder: 'Select a role',
			value: form.role,
			rules: { required: true },
			options: Roles.value
		}
	}))

	const tableEditOptions = computed(() => [
		{
			text: 'Edit',
			key: 'edit_btn',
			action: (member: TeamMembersResponse) => {
				if (member.status === 'pending') {
					return useNotification().alert({
						message: "You can't edit this user information cause it is still pending",
						status: 400
					})
				}
				navigateTo({
					path: `/settings/edit/${member.id}`,
					query: {
						type: 'team'
					}
				})
			}
		},
		{
			text: 'Disable Team Member',
			key: 'action_btn',
			action: (member: TeamMembersResponse) => {
				replace({
					query: {
						...query.value,
						member: member.id
					}
				})
				disableTeamModal.value = true
			},
			disabled: !hasAnyPermission.value([PERMISSIONS.PLAN.REMOVE, PERMISSIONS.PLAN.DEACTIVATE])
		}
	])

	const disableMember = async () => {
		if (!hasAnyPermission.value([PERMISSIONS.PLAN.REMOVE, PERMISSIONS.PLAN.DEACTIVATE])) {
			return useNotification().alert({
				status: 400,
				message: 'You are not authorized to remove a team member'
			})
		}
		isDisabling.value = true
		try {
			const { data, error } = await GetTeamMembers(
				'DELETE',
				{
					password: form.password
				},
				User.value?.customer.id
			)
			if (!error.value && data.value) {
				useNotification().alert({
					status: data.value.responseCode,
					message: data.value.responseText
				})
				disableTeamModal.value = false
				replace({
					query: {
						...query.value,
						member: ''
					}
				})
			}
			if (error.value) {
				form.password = ''
			}
		} finally {
			isDisabling.value = false
		}
	}

	const queryTeams = async () => {
		if (!User.value?.customer.id) {
			return null
		}
		try {
			loadingTable.value = true
			const team = await GetTeamMembers('GET', null, User.value?.customer.id || '', {
				search: search.value
			})
			if (team?.data.value?.data) {
				teamList.value = team.data.value.data
			}
		} finally {
			loadingTable.value = false
		}
	}
	const inviteTeam = async () => {
		isInvitingMember.value = true
		resolve(() => useMixpanel()?.track('invite_team.member_clicked'))
		try {
			const { data } = await InviteTeamMember('POST', {
				...form,
				callback_url: `${location.protocol}//${location.host}/invitation`
			})
			if (data.value) {
				resolve(() =>
					useMixpanel()?.track('invite_team.member_successful', {
						team_email: form.email,
						role: form.role_id
					})
				)
				resetFields()
				useNotification().alert({
					message: data.value.responseText,
					status: 200
				})
				queryTeams()
				emit('update:openInvite', false)
			} else {
				resolve(() => useMixpanel()?.track('invite_team.member_failed'))
			}
		} finally {
			isInvitingMember.value = false
		}
	}

	const getRecords = async () => {
		if (hasAnyPermission.value([PERMISSIONS.ROLE.VIEW])) {
			await GetAllRoles()
		}
		if (hasAnyPermission.value([PERMISSIONS.TEAM.VIEW_MEMBERS])) {
			await queryTeams()
		}
	}

	onMounted(getRecords)
	watch(User, getRecords)
	watch(
		() => props.openInvite,
		async () => await GetAllRoles(0)
	)
</script>

<template>
	<section class="mt-10 transition">
		<div class="mb-12 flex flex-wrap items-end justify-between gap-5">
			<div class="space-y-1">
				<h1 class="text-sm font-medium text-gray-700 sm:text-xl">Team Members</h1>
				<p class="font-normal text-gray-500">
					{{ teamList.length }} Member{{ (teamList.length > 1 && 's') || '' }}
				</p>
			</div>

			<forms-search-field v-model:value.trim="search" class="max-w-[380px] bg-white" @change="queryTeams" />
		</div>

		<shared-table-loader v-slot="{ columns }" :columns="teamColumns" :loading="loadingTable">
			<a-table
				:columns="columns"
				:data-source="teamList"
				:loading="loadingTable"
				class="overflow-hidden overflow-x-auto bg-white"
			>
				<template #bodyCell="{ column, text, index, record }">
					<span v-if="column.dataIndex === 'sn'">{{ index + 1 }}</span>
					<div v-else-if="column.dataIndex === 'full_name'" class="flex items-center gap-x-2">
						<div
							class="flex h-[35px] w-[35px] flex-none items-center justify-center rounded-full bg-gray-100 p-1 text-xs font-medium"
						>
							{{
								`${(record?.first_name && record?.first_name[0]) || ''} ${
									(record?.last_name && record?.last_name[0]) || 'N/A'
								}`
							}}
						</div>
						<span class="text-sm font-medium text-gray-600">
							{{ `${record?.first_name} ${record?.last_name}` }}
						</span>
					</div>

					<shared-badge
						v-else-if="column.dataIndex === 'status'"
						:text="record?.active ? 'active' : text"
						:active="record?.active"
						:pending="text === 'pending'"
						:class="{
							'bg-gray-200 font-medium text-gray-500': text === 'disabled'
						}"
					/>
					<span
						v-else-if="column.dataIndex === 'phone_number'"
						class="text-sm font-medium capitalize text-gray-600"
					>
						{{ text || 'N/A' }}
					</span>
					<span v-else-if="column.dataIndex === 'role'" class="text-sm font-medium capitalize text-gray-600">
						{{ text }}
					</span>

					<a-dropdown v-else-if="column.dataIndex === 'actions'" :trigger="['click']">
						<a class="ant-dropdown-link" @click.prevent>
							<button type="button">
								<lazy-tools-icon dir="system" icon="more" />
							</button>
						</a>
						<template #overlay>
							<a-menu class="default-shadow min-h-[48px] min-w-[200px] rounded-lg py-0">
								<a-menu-item
									v-for="item in tableEditOptions"
									:key="item.text"
									class="rounded-lg border-t px-5 py-4 first:border-t-0 hover:bg-gray-50"
									:class="{
										'font-medium text-red-500': item.key === 'action_btn',
										'text-gray-700': item.key === 'edit_btn'
									}"
									@click="item.action(record)"
								>
									<span class="text-sm capitalize">
										{{ item.text }}
									</span>
								</a-menu-item>
							</a-menu>
						</template>
					</a-dropdown>
					<span v-else>{{ text ?? 'N/A' }}</span>
				</template>
			</a-table>
		</shared-table-loader>

		<dialogs :show="openInvite" title="" @change="emit('update:openInvite', false)">
			<h1 class="text-lg font-semibold text-blue-950 md:text-[28px]">Invite Team Member</h1>

			<div class="mt-8">
				<a-form
					ref="invitationForm"
					:validate-messages="antValidateMessages"
					:model="form"
					autocomplete="off"
					@finish="inviteTeam"
				>
					<form-item-group tag="ul" class="space-y-3" :items="formItems" v-model="form" />
					<ui-button
						class="default-btn mt-12 w-full justify-center bg-primary-2"
						type="submit"
						:loading="isInvitingMember"
					>
						<span class="text-white"> Invite team member </span>
					</ui-button>
				</a-form>
			</div>
		</dialogs>

		<dialogs
			v-slot="{ close }"
			v-model:show="disableTeamModal"
			@change="replace({ query: { ...query, member: '' } })"
		>
			<div class="space-y-10">
				<div class="flex items-center justify-center">
					<lazy-tools-icon
						dir="other"
						icon="recycle"
						:height="32"
						:width="32"
						path-class="stroke-error-500"
						class="light-shadow rounded-full border border-gray-200 p-5"
					/>
				</div>

				<div
					class="flex flex-col items-center justify-center gap-y-3 space-y-2 rounded-md border bg-gray-50 py-6 md:px-8"
				>
					<h1 class="text-lg font-semibold text-red-500 md:text-[28px]">Deactivate Team Member</h1>

					<p class="text-center font-medium text-gray-500">
						Are you sure you want to disable this team member from your team?
					</p>
				</div>

				<a-form :model="form" autocomplete="off" layout="vertical" @finish="disableMember">
					<a-form-item class="!mb-5" label="Password" name="password" required>
						<a-input-password
							v-model:value.trim="form.password"
							autocomplete="new password"
							placeholder="Enter your password"
							class="custom-input !bg-gray-50 !px-5"
						/>
					</a-form-item>

					<div class="flex items-center justify-between space-x-10 pt-6">
						<a-button
							class="default-btn flex-1 justify-center bg-gray-200 !text-gray-900"
							:disabled="isDisabling"
							@click="() => close()"
						>
							Back
						</a-button>

						<a-button
							class="default-btn flex-1 justify-center !bg-red-500 !text-white"
							:loading="isDisabling"
							:disabled="isDisabling"
							@click="disableMember"
						>
							{{ isDisabling ? 'Disabling' : 'Disable' }}
						</a-button>
					</div>
				</a-form>
			</div>
		</dialogs>
	</section>
</template>
